import * as AccordionPrimitive from '@radix-ui/react-accordion'
import heroPhoto from '@cannect/assets/img/cuida/hero-cuida.webp'
import { RedirectUrls } from '@cannect/constants/texts'
import { Typography } from '@cannect/new-components/atoms'
import { CtaPill } from '@cannect/new-components/molecules'
import SEO from 'components/SEO'
import { LuCheck, LuMinusCircle, LuPlusCircle } from 'react-icons/lu'
import { CUIDA_FAQ_ITEMS, CUIDA_OFFERINGS, PATIENT_BENEFITS, PROFESSIONAL_BENEFITS } from './constants'

/** Eyebrow das seções: risco de 22x2 seguido do rótulo em caixa alta. */
const Eyebrow = ({ children, centered }: { children: string; centered?: boolean }) => (
  <p
    className={`m-0 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.13em] text-marketplace-800 ${
      centered ? 'justify-center' : ''
    }`}>
    <span aria-hidden className="inline-block h-[2px] w-[22px] shrink-0 rounded-full bg-marketplace-800" />
    {children}
  </p>
)

/**
 * Bullets das duas listas.
 *
 * Cheguei a usar os ícones próprios de cada item, que já existem em `assets/svg/cuida` — mas eles
 * são ilustrações e somem numa marca de 10px. O redesign usa um check simples em todos, que é o
 * que funciona nesse tamanho, e é o mesmo padrão da lista do Cannect Cuida na home.
 *
 * Contraste: `marketplace-800` sobre `marketplace-300` mede 4,79:1 no card claro; no card escuro
 * o check é `marketplace-300` sobre `neutral-50/20` composto com `marketplace-1000`.
 */
const BenefitList = ({ items, dark }: { items: { label: string }[]; dark?: boolean }) => (
  <ul className="m-0 flex list-none flex-col gap-[9px] p-0">
    {items.map((item) => (
      <li
        key={item.label}
        className={`flex items-start gap-[9px] text-sm ${dark ? 'text-neutral-50' : 'text-neutral-800'}`}>
        <span
          aria-hidden
          className={`mt-[1px] flex size-[18px] shrink-0 items-center justify-center rounded-full ${
            dark ? 'bg-neutral-50/20 text-marketplace-300' : 'bg-marketplace-300 text-marketplace-800'
          }`}>
          <LuCheck className="h-[10px] w-[10px]" strokeWidth={3} />
        </span>
        {item.label}
      </li>
    ))}
  </ul>
)

/**
 * Página do Cannect Cuida (`/cuida`), a partir do redesign entregue em HTML.
 *
 * Só o conteúdo: o header e o footer do redesign são placeholders genéricos do gerador e foram
 * descartados em favor dos globais do site.
 *
 * O arquivo traz quatro seções comentadas mas vazias — "Como funciona", "Time de enfermagem",
 * "Confiança" e o CTA final. Os dados de `steps` e `trustItems` existem no script mas não são
 * renderizados em lugar nenhum, então não foram construídos.
 *
 * Duas decisões de consistência com o resto do site, no lugar do que o HTML pedia:
 *
 * - **Tipografia de destaque**: o redesign usa Fraunces itálico nas palavras enfatizadas. O site
 *   já tem o itálico do Poppins como assinatura de marca (home, "Como funciona", Cannect Cuida),
 *   e carregar uma segunda família por três palavras numa página não se paga.
 * - **Paleta**: as cores do HTML são quase os tokens do projeto — `#263014` é `marketplace-1000`
 *   exato, `#526A32` fica a um passo de `marketplace-800` e `#F6F3EC` a três de `secondary-100/50`
 *   sobre `neutral-50`, que é o fundo que a home nova já usa. Estão mapeadas para os tokens.
 *
 * A foto do hero é 3:2 e leva os selos "Cuidado / Acolhimento / Segurança / Bem-estar" desenhados
 * na própria imagem, encostados na borda direita. Por isso ela não usa o `aspect-[5/4.5]` do
 * redesign nem `object-cover`: naquele enquadramento os selos ficavam de fora. O texto embutido
 * está descrito no `alt`, já que não é selecionável nem traduzível.
 */
export const Cuida = () => (
  <div className="w-full bg-neutral-50">
    <SEO
      title="Cannect Cuida — acompanhamento de enfermagem sem custo"
      description="Um time de enfermagem acompanha a evolução do seu tratamento, esclarece dúvidas e dá mais segurança à sua jornada, sempre em conjunto com o seu médico ou cirurgião-dentista."
      canonical="/cuida"
      keywords="cannect cuida, acompanhamento de enfermagem, adesão ao tratamento, cannabis medicinal, suporte ao paciente"
    />

    <section aria-labelledby="cuida-hero-heading" className="w-full bg-secondary-100/50">
      <div className="mx-auto grid w-full max-w-[1230px] items-center gap-10 px-8 py-16 lg:grid-cols-2">
        <div className="flex flex-col">
          <Eyebrow>Cuida</Eyebrow>

          <Typography.Heading
            id="cuida-hero-heading"
            level={1}
            weight="semibold"
            className="m-0 mt-[14px] text-[32px] leading-[1.15] tracking-[-0.022em] text-marketplace-1000">
            Seu tratamento vai muito além da entrega do medicamento.
          </Typography.Heading>

          <p className="m-0 mt-4 max-w-[520px] text-base leading-[1.6] text-neutral-700">
            Nossa equipe multidisciplinar acompanha a evolução do seu tratamento com acolhimento, esclarece dúvidas e
            oferece mais segurança durante toda a sua jornada, sempre em conjunto com o seu médico ou
            cirurgião-dentista, tudo isso sem custo adicional para você.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <CtaPill
              variant="solidDark"
              external
              title="Quero fazer parte do programa"
              path={RedirectUrls.WHATSAPP_CUIDA_ONBOARDING}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-[28px] bg-gradient-to-br from-marketplace-300 to-marketplace-700 lg:max-h-[520px]">
          <img
            src={heroPhoto}
            alt="Enfermeiro conversando com uma paciente durante o acompanhamento do tratamento, ao lado dos selos Cuidado, Acolhimento, Segurança e Bem-estar"
            className="h-full w-full object-contain"
          />
        </div>
      </div>
    </section>

    <section aria-labelledby="cuida-audiences-heading" className="w-full bg-secondary-100/50">
      <div className="mx-auto flex w-full max-w-[1230px] flex-col px-8 py-[76px]">
        <Eyebrow>Para quem é o Cuida</Eyebrow>

        <Typography.Heading
          id="cuida-audiences-heading"
          level={2}
          weight="semibold"
          className="m-0 mt-[14px] max-w-[700px] text-[28px] leading-[1.2] tracking-[-0.015em] text-marketplace-1000">
          {/* `span`, não `em`: o itálico é assinatura de marca, não ênfase semântica. */}
          Feito para <span className="font-medium italic">pacientes</span> e para{' '}
          <span className="font-medium italic">profissionais de saúde</span>.
        </Typography.Heading>

        <div className="mt-9 grid gap-5 lg:grid-cols-2">
          <div className="flex flex-col rounded-[28px] border border-solid border-marketplace-1000/10 bg-neutral-50 p-8">
            <h3 className="m-0 text-lg font-semibold text-marketplace-1000">Para pacientes</h3>

            <p className="m-0 mt-2 text-sm leading-[1.6] text-neutral-700">
              O programa Cuida garante o cuidado coordenado para que você tenha o suporte necessário durante todo o
              tratamento, em alinhamento com as orientações do seu médico ou cirurgião-dentista.
            </p>

            <div className="mt-5">
              <BenefitList items={PATIENT_BENEFITS} />
            </div>
          </div>

          <div className="flex flex-col rounded-[28px] bg-marketplace-1000 p-8">
            <h3 className="m-0 text-lg font-semibold text-neutral-50">Para profissionais de saúde</h3>

            <p className="m-0 mt-2 text-sm leading-[1.6] text-neutral-50/80">
              Acompanhe a evolução dos seus pacientes prescritos em conjunto com o time de enfermagem, com visibilidade
              sobre o tratamento a cada etapa.
            </p>

            <div className="mt-5">
              <BenefitList items={PROFESSIONAL_BENEFITS} dark />
            </div>

            <p className="m-0 mt-5 text-xs leading-[1.5] text-neutral-50/70">
              É importante destacar que{' '}
              <span className="italic">nossa equipe não realiza qualquer ajuste na dosagem</span> prescrita por você,
              nem interferimos no uso de outros medicamentos em uso concomitante.
            </p>

            <div className="mt-6 flex">
              <CtaPill
                variant="solid"
                title="Quero saber mais sobre"
                path={RedirectUrls.WHATSAPP_CUIDA_PROFESSIONALS}
                external
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section aria-labelledby="cuida-offerings-heading" className="w-full bg-neutral-50">
      <div className="mx-auto flex w-full max-w-[1230px] flex-col px-8 py-[76px]">
        <Eyebrow>O que o Cuida oferece</Eyebrow>

        <Typography.Heading
          id="cuida-offerings-heading"
          level={2}
          weight="semibold"
          className="m-0 mt-[14px] max-w-[640px] text-[28px] leading-[1.2] tracking-[-0.015em] text-marketplace-1000">
          Continuidade em cada etapa
        </Typography.Heading>

        <ul className="m-0 mt-9 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {CUIDA_OFFERINGS.map((offering) => (
            <li key={offering.title} className="flex gap-[13px] rounded-[18px] bg-secondary-100/50 p-5">
              <span
                aria-hidden
                className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-marketplace-300 text-marketplace-800">
                <LuCheck className="size-[18px]" strokeWidth={2.5} />
              </span>

              <div className="flex flex-col gap-1">
                <h3 className="m-0 text-base font-semibold text-marketplace-1000">{offering.title}</h3>
                <p className="m-0 text-sm leading-[1.5] text-neutral-700">{offering.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section aria-labelledby="cuida-faq-heading" className="w-full bg-secondary-100/50">
      <div className="mx-auto flex w-full max-w-[840px] flex-col px-8 py-[76px]">
        <div className="flex flex-col items-center text-center">
          <Eyebrow centered>Dúvidas frequentes</Eyebrow>

          <Typography.Heading
            id="cuida-faq-heading"
            level={2}
            weight="semibold"
            className="m-0 mt-[14px] text-[28px] leading-[1.2] tracking-[-0.015em] text-marketplace-1000">
            Dúvidas sobre o <span className="font-medium italic">Cuida</span>
          </Typography.Heading>
        </div>

        {/* Mesmo padrão do FAQ da home: primitivos do Radix, `collapsible` e ícones de +/− em
            círculo. O redesign abre o primeiro item por padrão. */}
        <AccordionPrimitive.Root
          type="single"
          collapsible
          defaultValue="cuida-faq-0"
          className="mt-8 flex w-full flex-col">
          {CUIDA_FAQ_ITEMS.map((item, index) => (
            <AccordionPrimitive.Item
              key={item.question}
              value={`cuida-faq-${index}`}
              className="w-full border-0 border-b border-solid border-marketplace-1000/[0.12]">
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger className="group flex min-h-[56px] w-full cursor-pointer appearance-none items-center justify-between gap-4 border-0 bg-transparent px-1 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2">
                  <span className="text-base font-semibold text-marketplace-1000">{item.question}</span>
                  <LuPlusCircle
                    aria-hidden
                    className="size-5 shrink-0 text-marketplace-800 group-data-[state=open]:hidden"
                  />
                  <LuMinusCircle
                    aria-hidden
                    className="hidden size-5 shrink-0 text-marketplace-800 group-data-[state=open]:block"
                  />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>

              <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <p className="m-0 max-w-[700px] px-1 pb-[22px] text-sm leading-[1.55] text-neutral-700">
                  {item.answer}
                </p>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          ))}
        </AccordionPrimitive.Root>
      </div>
    </section>
  </div>
)

export default Cuida
